import React, { useEffect, useRef } from "react";
import { Instagram, ExternalLink } from "lucide-react";

/**
 * Extracts Instagram post/reel ID from any IG url:
 *  https://www.instagram.com/p/SHORTCODE/      -> { type: 'post', id: SHORTCODE }
 *  https://www.instagram.com/reel/SHORTCODE/   -> { type: 'reel', id: SHORTCODE }
 *  https://www.instagram.com/reels/SHORTCODE/  -> { type: 'reel', id: SHORTCODE }
 *  https://www.instagram.com/tv/SHORTCODE/     -> { type: 'igtv', id: SHORTCODE }
 */
const parseInstagramUrl = (url) => {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
  if (!m) return null;
  const type = m[1] === "p" ? "post" : m[1] === "tv" ? "igtv" : "reel";
  return { type, id: m[2] };
};

const extractHandle = (url) => {
  if (!url) return "";
  const m = url.match(/instagram\.com\/([A-Za-z0-9_.]+)\/?/);
  if (m && !/^(p|reel|reels|tv|explore|accounts)$/.test(m[1])) return m[1];
  return url.replace(/^@/, "").replace(/^https?:\/\//, "").replace(/\/$/, "");
};

const normalizeIgUrl = (url) => {
  // Strip query strings and trailing slashes; ensure https.
  const clean = url.split("?")[0].replace(/\/?$/, "/");
  return clean.startsWith("http") ? clean : `https://www.instagram.com/${clean}`;
};

/**
 * Loads Instagram's official embed.js once and re-runs `window.instgrm.Embeds.process()`
 * whenever the dependency array changes. This converts <blockquote class="instagram-media">
 * markers into rich interactive embeds with native inline playback (incl. Reels).
 */
const useInstagramEmbeds = (deps) => {
  useEffect(() => {
    const process = () => {
      try {
        window.instgrm?.Embeds?.process();
      } catch {
        /* no-op */
      }
    };
    if (window.instgrm) {
      // Slight delay to allow DOM to settle (modal animation, etc.)
      const t = setTimeout(process, 30);
      return () => clearTimeout(t);
    }
    const existing = document.querySelector('script[src*="instagram.com/embed.js"]');
    if (existing) {
      existing.addEventListener("load", process, { once: true });
      // In case it's already loaded but instgrm isn't ready yet
      const i = setInterval(() => {
        if (window.instgrm) {
          clearInterval(i);
          process();
        }
      }, 200);
      return () => {
        existing.removeEventListener("load", process);
        clearInterval(i);
      };
    }
    const s = document.createElement("script");
    s.src = "https://www.instagram.com/embed.js";
    s.async = true;
    s.defer = true;
    s.onload = process;
    document.body.appendChild(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps || []);
};

const InstagramEmbed = ({ url }) => {
  const parsed = parseInstagramUrl(url);
  const cleanUrl = parsed
    ? `https://www.instagram.com/${parsed.type === "post" ? "p" : parsed.type === "igtv" ? "tv" : "reel"}/${parsed.id}/`
    : null;
  if (!parsed || !cleanUrl) return null;

  const isVertical = parsed.type === "reel" || parsed.type === "igtv";

  // Container sized to Instagram's natural embed width (~400px) with reel-friendly height
  // Reels: vertical 9:16 video + ~110px header/footer chrome from IG
  // Posts: square / 4:5 + ~150px header/footer chrome
  const minHeight = isVertical ? 720 : 640;
  const maxWidth = isVertical ? 420 : 540;

  return (
    <div
      className="ig-embed-wrap mx-auto w-full"
      style={{ maxWidth, minHeight }}
    >
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={cleanUrl}
        data-instgrm-version="14"
        data-instgrm-captioned=""
        style={{
          background: "#000",
          border: "1px solid rgba(184, 115, 51, 0.25)",
          borderRadius: "2px",
          margin: "0",
          padding: "0",
          maxWidth: "100%",
          minWidth: "320px",
          width: "100%",
        }}
      >
        <a href={cleanUrl} target="_blank" rel="noreferrer noopener" style={{ color: "#B87333", padding: "16px", display: "block", textDecoration: "none", fontFamily: "Montserrat, sans-serif", fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase" }}>
          View on Instagram →
        </a>
      </blockquote>
    </div>
  );
};

const InstagramFeed = ({ profileUrl, posts = [], artistName }) => {
  const validPosts = (posts || []).filter((p) => parseInstagramUrl(p));
  const handle = extractHandle(profileUrl || "");
  const fullProfileUrl = profileUrl
    ? (profileUrl.startsWith("http") ? normalizeIgUrl(profileUrl) : `https://www.instagram.com/${handle}/`)
    : (handle ? `https://www.instagram.com/${handle}/` : null);

  // Instagram embed.js — process whenever post list changes
  useInstagramEmbeds([validPosts.join("|")]);

  // Re-process again on a slight delay to handle modal mount animations
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const t1 = setTimeout(() => window.instgrm?.Embeds?.process(), 400);
    const t2 = setTimeout(() => window.instgrm?.Embeds?.process(), 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [validPosts.length]);

  if (!validPosts.length && !fullProfileUrl) return null;

  return (
    <div ref={containerRef}>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-px w-8 bg-gold/60" />
        <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase flex items-center gap-2">
          <Instagram size={11} />
          Instagram Feed
        </span>
        {fullProfileUrl && handle && (
          <a
            href={fullProfileUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="ml-auto inline-flex items-center gap-1.5 text-gold/70 hover:text-gold text-[0.65rem] tracking-widest uppercase transition-colors"
          >
            @{handle}
            <ExternalLink size={10} />
          </a>
        )}
      </div>

      {validPosts.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 justify-items-center">
          {validPosts.map((url) => (
            <InstagramEmbed key={url} url={url} />
          ))}
        </div>
      ) : (
        fullProfileUrl && (
          <a
            href={fullProfileUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="glass rounded-sm p-8 flex items-center gap-5 hover:border-gold transition-all group"
            style={{ display: "flex" }}
          >
            <div className="w-16 h-16 rounded-sm border border-gold/40 flex items-center justify-center group-hover:border-gold transition-colors flex-shrink-0">
              <Instagram size={28} className="text-gold" />
            </div>
            <div className="flex-1">
              <p className="font-body text-white/40 text-[0.6rem] tracking-[0.3em] uppercase mb-1">Follow on Instagram</p>
              <p className="font-display text-2xl text-white">{handle ? `@${handle}` : "View Profile"}</p>
              <p className="font-body text-white/40 text-xs mt-1">{artistName ? `See ${artistName}'s latest posts` : "See their latest posts"}</p>
            </div>
            <ExternalLink size={16} className="text-gold/60 group-hover:text-gold transition-colors" />
          </a>
        )
      )}
    </div>
  );
};

export default InstagramFeed;
